import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import { FaExpand, FaTimes } from 'react-icons/fa';
import { resolveProductMediaUrl } from '../../../services/apiService';
import { useThemeClasses } from '../../../hooks/useThemeClasses';
import ProvenanceBadge from './ProvenanceBadge';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

const PLACEHOLDER =
  'https://via.placeholder.com/800x600?text=Condition+Photo';

const GALLERY_STYLES = `
  .used-part-gallery {
    width: 100%;
    height: 100%;
  }
  .used-part-gallery .swiper-slide {
    display: flex;
    align-items: stretch;
    justify-content: center;
    height: 100%;
  }
  .used-part-gallery .swiper-button-next,
  .used-part-gallery .swiper-button-prev {
    width: 2.25rem;
    height: 2.25rem;
    margin-top: -1.125rem;
    border-radius: 9999px;
    background: rgba(15, 23, 42, 0.75);
    border: 1px solid rgba(148, 163, 184, 0.35);
    color: #fbbf24;
  }
  .used-part-gallery .swiper-button-next:after,
  .used-part-gallery .swiper-button-prev:after {
    font-size: 0.75rem;
    font-weight: 700;
  }
  .used-part-gallery .swiper-button-next:hover,
  .used-part-gallery .swiper-button-prev:hover {
    background: rgba(15, 23, 42, 0.92);
    color: #fcd34d;
  }
  .used-part-gallery .swiper-pagination-bullet {
    background: rgba(148, 163, 184, 0.5);
    opacity: 1;
  }
  .used-part-gallery .swiper-pagination-bullet-active {
    background: #f59e0b;
  }
`;

function GallerySlides({ images, fullscreen = false }) {
  return (
    <Swiper
      modules={[Navigation, Pagination, Zoom]}
      spaceBetween={0}
      slidesPerView={1}
      navigation
      pagination={{ clickable: true }}
      zoom={{ maxRatio: 3 }}
      className={`used-part-gallery ${fullscreen ? 'h-full max-h-[90vh]' : 'h-full'}`}
    >
      {images.map((src, i) => (
        <SwiperSlide key={`${src}-${i}`}>
          <div className="swiper-zoom-container relative h-full w-full bg-slate-950">
            <img
              src={src}
              alt={`Condition photo ${i + 1}`}
              className="absolute inset-0 h-full w-full object-cover object-center"
              loading={i === 0 ? 'eager' : 'lazy'}
            />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

export default function UsedPhotoGallery({
  photos = [],
  minRequired = 3,
  provenance = null,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const { isDark } = useThemeClasses();

  const images =
    photos.length > 0
      ? photos.map((u) => resolveProductMediaUrl(u) || PLACEHOLDER)
      : [PLACEHOLDER];

  const handleSlideChange = (swiper) => {
    setActiveIndex(swiper.activeIndex);
  };

  return (
    <>
      <style>{GALLERY_STYLES}</style>
      <div
        className={`overflow-hidden rounded-2xl border shadow-lg ${
          isDark
            ? 'border-slate-700/60 bg-slate-900/50 shadow-black/20'
            : 'border-slate-200 bg-white shadow-slate-200/60'
        }`}
      >
        <div className="relative aspect-[4/3] w-full sm:aspect-[5/4] lg:aspect-[4/3]">
          <Swiper
            modules={[Navigation, Pagination, Zoom]}
            spaceBetween={0}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            zoom={{ maxRatio: 3 }}
            onSlideChange={handleSlideChange}
            className="used-part-gallery h-full w-full"
          >
            {images.map((src, i) => (
              <SwiperSlide key={`${src}-${i}`}>
                <div className="swiper-zoom-container relative h-full w-full bg-slate-950">
                  <img
                    src={src}
                    alt={`Condition photo ${i + 1}`}
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="absolute right-3 top-3 z-10 rounded-full border border-slate-600/80 bg-slate-900/80 p-2.5 text-slate-200 backdrop-blur-sm hover:bg-slate-800"
            aria-label="Fullscreen"
          >
            <FaExpand className="text-sm" />
          </button>

          {photos.length < minRequired && (
            <p className="absolute left-3 top-3 z-10 rounded-md bg-amber-500 px-2.5 py-1 text-[10px] font-bold text-slate-900 shadow">
              {photos.length}/{minRequired} photos
            </p>
          )}
        </div>

        <div className={`flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 ${
          isDark ? 'border-slate-700/60 bg-slate-900/80' : 'border-slate-200 bg-slate-50'
        }`}>
          <div className="flex flex-wrap items-center gap-2">
            {provenance?.label ? <ProvenanceBadge provenance={provenance} /> : null}
          </div>
          {images.length > 1 && (
            <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Photo {activeIndex + 1} of {images.length}
            </span>
          )}
        </div>
      </div>

      {fullscreen && (
        <div className="fixed inset-0 z-[130] flex flex-col bg-black/95">
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="absolute right-4 top-4 z-20 rounded-full border border-slate-600 bg-slate-900/90 p-3 text-white"
            aria-label="Close fullscreen"
          >
            <FaTimes />
          </button>
          <div className="flex flex-1 items-center justify-center p-4 md:p-8">
            <div className="h-full w-full max-w-5xl">
              <GallerySlides images={images} fullscreen />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
